import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { SubscribeManager } from '../class/subscribe-manager.class';
import { SiteLocation, LocationsService } from './locationServices';
import { EnvironmentalDataSourcesService } from './environmentalDataSourceServices';
import { safeJsonParse } from '../helpers/dataHelpers';

export type Site = {
  id: string;
  name: string;
  /** Stable identifier used to build site-scoped admin URLs. */
  awsIdentifier?: string;
  longitude?: number;
  latitude?: number;
  siteArea?: number;
  locations: SiteLocation[];
};

@Injectable({
  providedIn: 'root',
})
export class SitesService extends SubscribeManager<Site> {
  private siteSubject: BehaviorSubject<Site | null>;

  constructor(
    private locationsService: LocationsService,
    private environmentalDataSourcesService: EnvironmentalDataSourcesService
  ) {
    super();
    const storedSite = localStorage.getItem('selectedSite');
    const initialSite: Site | null = safeJsonParse(storedSite);
    this.siteSubject = new BehaviorSubject<Site | null>(initialSite);
  }

  // What if our available sites have changed and we no longer have access
  // to the site we're on?
  public checkAvailableSites(sites: Site[]): void {
    const currentSiteId = this.siteSubject.getValue()?.id;
    const allowedSiteIds = sites.map((site) => site.id);

    if (!currentSiteId || !allowedSiteIds.includes(currentSiteId)) {
      if (allowedSiteIds.length > 0) {
        this.setSelectedSite(sites[0]);
      } else {
        this.clearSelectedSite();
      }
    }
  }

  // This makes sure our local storage is in sync with the latest site data
  public updateSitesData(sites: Site[]): void {
    const currentSiteId = this.siteSubject.getValue()?.id;
    const currentSite = sites.find((site) => site.id === currentSiteId);
    if (currentSite) {
      this.setSelectedSite(currentSite);
    }
  }

  public getSelectedSite(): Observable<Site | null> {
    return this.siteSubject.asObservable();
  }

  public setSelectedSite(site: Site): void {
    this.siteSubject.next(site);
    localStorage.setItem('selectedSite', JSON.stringify(site));
    this.locationsService.setLocations(site.locations);
    this.environmentalDataSourcesService.refreshEnvironmentalDataSources(site.id);
    this.pushChange(site);
  }

  public clearSelectedSite(): Observable<null> | null {
    localStorage.removeItem('selectedSite');
    this.locationsService.clearLocations();
    this.environmentalDataSourcesService.clearEnvironmentalDataSources();
    this.siteSubject.next(null);
    return null;
  }
}
